import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { styled, Typography } from 'opensolar-ui'
import { memo, useCallback } from 'react'

export type SystemSelectDropdownOptionType = {
  id: number
  name: string
}

export type SystemSelectDropdownPropsType = {
  selectedSystemId: number | undefined
  disabled?: boolean
  systemOptions: SystemSelectDropdownOptionType[]
  onSelect: (id: number) => void
}

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '14px',
  width: '100%',
  '& .MuiSelect-select': {
    padding: '8px 32px 8px 12px',
    backgroundColor: 'white',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[400],
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}))

const StyledMenuItem = styled(MenuItem)({
  fontSize: '14px',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
})

const TextWrapper = styled(Typography)({
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
})

const SystemSelectDropdown = memo(
  ({ selectedSystemId, systemOptions, disabled, onSelect }: SystemSelectDropdownPropsType) => {
    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const id = event.target.value as number
      onSelect(id)
    }

    const handleOnOpen = useCallback(() => {
      logAmplitudeEvent(
        'generic_button_clicked',
        {
          key: 'pages.shop.multi_project_ordering',
          action: 'select',
          source: 'system_option',
          context: 'system_option_opened',
        },
        'Hardware BOM'
      )
    }, [])

    if (systemOptions.length === 0) {
      return <TextWrapper variant="body2">－</TextWrapper>
    }

    if (systemOptions.length === 1) {
      return <TextWrapper variant="body2">{systemOptions[0].name}</TextWrapper>
    }

    return (
      <StyledSelect
        variant="outlined"
        value={selectedSystemId || ''}
        disabled={disabled}
        onOpen={handleOnOpen}
        displayEmpty
        onChange={handleChange}
      >
        <StyledMenuItem value={''} disabled>
          Choose a system
        </StyledMenuItem>
        {systemOptions.map((option: SystemSelectDropdownOptionType) => {
          return (
            <StyledMenuItem key={option.id} value={option.id}>
              {option.name}
            </StyledMenuItem>
          )
        })}
      </StyledSelect>
    )
  }
)

export default SystemSelectDropdown
